import { type DocumentInfo, ErrorResponse, type ErrorType } from 'api';
import { revokeObjectUrl } from 'features';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface DocumentContextValue {
  document: DocumentInfo;
  isLoading: boolean;
  error: ErrorType<ErrorResponse> | null;
  fileUrl: string;
  setFileUrl: (fileUrl: string) => void;
}

const DocumentContext = createContext<DocumentContextValue | null>(null);

interface DocumentProviderProps {
  document: DocumentInfo;
  isLoading: boolean;
  error: ErrorType<ErrorResponse> | null;
}

export const DocumentProvider = ({
  children,
  document,
  isLoading,
  error,
}: PropsWithChildren<DocumentProviderProps>) => {
  const [fileUrl, setCurrentFileUrl] = useState<string>(document.file_url || '');
  const currentFileUrlRef = useRef(fileUrl);

  const setFileUrl = useCallback((nextFileUrl: string) => {
    setCurrentFileUrl((currentFileUrl) => {
      if (currentFileUrl !== nextFileUrl) {
        revokeObjectUrl(currentFileUrl);
      }

      return nextFileUrl;
    });
  }, []);

  useEffect(() => {
    currentFileUrlRef.current = fileUrl;
  }, [fileUrl]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(currentFileUrlRef.current);
    };
  }, []);

  const value = useMemo(() => {
    return {
      document,
      error,
      fileUrl,
      isLoading,
      setFileUrl,
    };
  }, [document, isLoading, error, fileUrl, setFileUrl]);

  return <DocumentContext.Provider value={value}>{children}</DocumentContext.Provider>;
};

export const useDocument = (): DocumentContextValue => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocument must be used within a DocumentProvider');
  }
  return context;
};
